import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Form } from 'antd'
import fetchAPI from 'src/lib/utils/fetch-api'
import { getPreparedSchema } from '../../../lib/utils/schema'
import localizedServerErrors from '../../../lib/utils/localized-server-errors'
import { AdminCollectionItemConnector, IFromData, initialFormDataValues } from '../../connectors/admin-collection-item'
//import { CascaderValueType } from 'antd/es/cascader'
import B2BLayout from '../../layouts/b2b'
import { EditCollectionForm } from '../../forms/app-edit-collection-form'

function getExtractedSchema(value: Array<string>): Array<any> {
  return value ? value.map(item => item.split('/')) : [undefined]
}

export default function AdminEditCollectionPage() {
  const navigate = useNavigate()
  const { id } = useParams() as any
  const [selectedSchemaForCascader, setSelectedSchemaForCascader] = useState<any[]>([])
  const [pageErrors, setPageErrors] = useState<string[]>([])
  const [formData, setFormData] = useState<IFromData>(initialFormDataValues)
  const [accessConfig, setAccessConfig] = useState<Record<string, any>>({})
  const [form] = Form.useForm()
  const breadcrumbs = useMemo(() => [
    {
      href: '/admin',
      title: 'Администрирование',
    },
    {
      href: '/admin/collections',
      title: 'Справочники',
    },
    {
      title: formData.title
    }
  ], [formData.title])

  // @todo Extract to Redux Store to avoid double call
  useEffect(() => {
    const getCollectionData = async () => {
      try {
        const response = await fetchAPI(`/api/collections/${id}/metadata`)

        if (typeof response['data'] === 'object') {
          const data = response['data']
          const schema = getExtractedSchema(data['schema'])
          const access = data['access'] && typeof data['access'] === 'object' ? data['access'] : {}
          const permissions = Array.isArray(access.permissions)
            ? access.permissions.filter(item => typeof item === 'string')
            : typeof access.permission === 'string'
              ? [access.permission]
              : []
          const formValue = {
            name: data['name'],
            title: data['title'],
            'post-function': data['post-function'],
            description: data['description'] || data['title'],
            group_id: data['group_id'],
            schema,
            access_permissions: permissions,
          }

          form.setFieldsValue(formValue)
          setFormData(formValue)
          setAccessConfig(access)
          setSelectedSchemaForCascader(schema)
          setPageErrors([])
        } else {
          setPageErrors([
            'Некорректный ответ сервера'
          ])
        }
      } catch(err) {
        setPageErrors([
          'Ошибка загрузки данных: ' + err
        ])
      }
    }

    getCollectionData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onFinish = async values => {
    //Удалим пробелы и табуляцию, если есть
    const newName = String(values.name).trim()
    if(values.name !== newName){
      values.name = newName
      form.setFieldsValue({ name: newName })
    }
    const preparedSchema = values.schema && getPreparedSchema(values.schema)
    const accessPermissions = Array.isArray(values.access_permissions)
      ? values.access_permissions.filter(item => typeof item === 'string' && item.trim().length > 0)
      : []
    const nextAccessConfig = { ...accessConfig }
    if (accessPermissions.length > 0) {
      nextAccessConfig.permissions = accessPermissions
    } else {
      delete nextAccessConfig.permissions
    }
    const hasAccessConfig = Object.keys(nextAccessConfig).length > 0
    const requestBody = {
      'data': {
        'name': values.name ? `${values.name}`.toLowerCase() : '',
        'title': values.title,
        'description': values.description,
        'post-function': values['post-function'] ?? '',
        'schema': preparedSchema,
        'group_id': values['group_id'],
        ...(hasAccessConfig ? { access: nextAccessConfig } : {}),
      }
    }
    const result = await fetchAPI(`/api/collections/${values.name}/metadata`, {
      method: 'POST',
      body: JSON.stringify(requestBody),
    })
    if (result['data'] && result['data']['updated-at']) {
      setPageErrors([])
      navigate('/admin/collections')
    } else {
      const errors =
        ((Array.isArray(result['errors'])) && (result['errors'].length > 0))
        ? result['errors'].map(error => localizedServerErrors(error['detail']))
        : ['Неизвестная ошибка при выполнении запроса']
      setPageErrors(errors)
    }
  }

  return (
    <B2BLayout breadcrumbs={breadcrumbs}>
      {AdminCollectionItemConnector({
        title: 'Редактирование справочника',
        isEdit: true,
        form,
        formData,
        setFormData,
        pageErrors,
        initSelectedSchemaForCascader: selectedSchemaForCascader,
        onFinish,
      })(EditCollectionForm)}
    </B2BLayout>
  )
}
